import { useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  FileDown,
  Check,
  Lock,
  CheckCircle2,
  PlayCircle,
} from 'lucide-react';
import { Button } from '@/client/components/ui/button';
import { Markdown } from '@/client/components/cms/Markdown';
import { api, ApiError } from '@/client/lib/api';
import { isLocale, type Locale } from '@/client/i18n';
import type { PortalCourseDetail } from '@/client/types/courses';
import { EpisodePlayer } from '@/client/features/courses/EpisodePlayer';

export function PortalLessonPage() {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const slug = params.slug ?? '';
  const orderParam = Number.parseInt(params.order ?? '', 10);
  const locale: Locale = isLocale(params.locale) ? params.locale : 'sk';

  const query = useQuery({
    queryKey: ['portal', 'course', slug],
    queryFn: () => api<PortalCourseDetail>(`/api/v1/me/enrollments/${slug}`),
    enabled: Boolean(slug),
    retry: false,
  });

  const episode = query.data?.episodes.find((e) => e.displayOrder === orderParam) ?? null;

  useEffect(() => {
    if (query.data?.course.title && episode) {
      document.title = `${episode.title} | ${query.data.course.title}`;
    }
  }, [query.data?.course.title, episode]);

  const markCompleteMutation = useMutation({
    mutationFn: async () => {
      if (!episode) return;
      // Manual completion: report watchedSeconds equal to the duration so the
      // server's >=95% threshold flips the row. If duration is unknown (no
      // video uploaded), just report the current accumulated value + 1 so the
      // server still records activity, but completion only triggers when the
      // duration is set.
      const target = episode.videoDurationSeconds
        ? episode.videoDurationSeconds
        : episode.watchedSeconds + 1;
      await api(`/api/v1/episodes/${episode.id}/progress`, {
        method: 'POST',
        body: { watchedSeconds: target },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal', 'course', slug] });
      queryClient.invalidateQueries({ queryKey: ['portal', 'enrollments'] });
    },
  });

  if (query.isLoading) {
    return (
      <div className="space-y-4">
        <div className="dr-card-static h-12 animate-shimmer" />
        <div className="dr-card-static aspect-video animate-shimmer" />
        <div className="dr-card-static h-24 animate-shimmer" />
      </div>
    );
  }

  if (query.isError) {
    if (query.error instanceof ApiError && query.error.status === 404) {
      return (
        <div className="dr-card-static p-12 text-center">
          <h1 className="font-display text-h2 text-primary-dark">
            {t('errors.not_found_title')}
          </h1>
          <Link
            to={`/${locale}/portal/kurzy`}
            className="mt-6 inline-flex items-center gap-1.5 font-sans text-body-small font-medium text-primary hover:gap-2.5 transition-all"
          >
            <ArrowLeft size={14} />
            {t('portal.nav_courses')}
          </Link>
        </div>
      );
    }
    return (
      <div className="dr-card-static p-6 font-sans text-body text-error">
        {t('errors.generic')}
      </div>
    );
  }

  if (!query.data) return null;

  if (!episode) {
    return (
      <div className="dr-card-static p-12 text-center">
        <h1 className="font-display text-h3 text-primary-dark">{t('errors.not_found_title')}</h1>
        <Link
          to={`/${locale}/portal/kurzy/${slug}`}
          className="mt-6 inline-flex items-center gap-1.5 font-sans text-body-small font-medium text-primary hover:gap-2.5 transition-all"
        >
          <ArrowLeft size={14} />
          {t('player.back_to_course')}
        </Link>
      </div>
    );
  }

  const { course, episodes } = query.data;
  const currentIndex = episodes.findIndex((e) => e.id === episode.id);
  const prevEpisode = currentIndex > 0 ? episodes[currentIndex - 1] : null;
  const nextEpisode =
    currentIndex >= 0 && currentIndex < episodes.length - 1 ? episodes[currentIndex + 1] : null;

  const isCompleted = episode.status === 'completed';
  const isLocked = episode.status === 'locked';

  return (
    <div className="space-y-6 animate-fade-up">
      {/* Breadcrumb / back */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Link
          to={`/${locale}/portal/kurzy/${slug}`}
          className="inline-flex items-center gap-1.5 font-sans text-body-small font-medium text-primary hover:gap-2.5 transition-all"
        >
          <ArrowLeft size={14} />
          {t('player.back_to_course')}
        </Link>
        <span className="inline-flex items-center gap-2 rounded-pill bg-white/70 backdrop-blur-md border border-glass-border-strong px-3 h-8 font-sans text-caption text-text-secondary">
          {course.title}
        </span>
      </div>

      {/* Header */}
      <header className="dr-card-static relative overflow-hidden p-6 sm:p-8">
        <div
          className="absolute -top-20 -right-20 w-72 h-72 rounded-full pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(193,167,226,0.30) 0%, transparent 65%)',
            filter: 'blur(40px)',
          }}
          aria-hidden
        />
        <div className="relative flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="min-w-0">
            <p className="dr-eyebrow">
              {t('courses.episode_label', { number: episode.displayOrder })}
            </p>
            <h1 className="mt-2 font-display text-h2 text-primary-dark">{episode.title}</h1>
          </div>
          <div className="shrink-0">
            {isCompleted ? (
              <span className="inline-flex items-center gap-1.5 rounded-pill border border-success/25 bg-success/12 px-3 py-1.5 font-sans text-caption font-medium text-success">
                <CheckCircle2 size={14} />
                {t('courses.portal_episode_completed')}
              </span>
            ) : isLocked ? (
              <span className="inline-flex items-center gap-1.5 rounded-pill border border-warning/30 bg-warning/12 px-3 py-1.5 font-sans text-caption font-medium text-[#7a5500]">
                <Lock size={14} />
                {t('courses.portal_episode_locked', { count: episode.unlockAfterDays })}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-pill border border-primary/25 bg-primary-light/30 px-3 py-1.5 font-sans text-caption font-medium text-primary-dark">
                <PlayCircle size={14} />
                {t('courses.open_lesson')}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Video / locked / no-video */}
      {isLocked && episode.unlocksAt ? (
        <section className="dr-card-static relative overflow-hidden p-10 sm:p-14 text-center">
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'linear-gradient(135deg, rgba(212,150,10,0.08) 0%, transparent 60%)',
            }}
            aria-hidden
          />
          <div className="relative">
            <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-warning/20 to-warning/5 border border-warning/25 text-warning">
              <Lock size={24} />
            </div>
            <h2 className="mt-5 font-display text-h3 text-[#7a5500]">
              {t('player.locked_title')}
            </h2>
            <p className="mt-3 font-sans text-body text-text-secondary max-w-md mx-auto leading-relaxed">
              {t('player.locked_body', { date: formatUnlockDate(episode.unlocksAt, locale) })}
            </p>
          </div>
        </section>
      ) : !episode.hasVideo ? (
        <section className="rounded-2xl border-2 border-dashed border-glass-border-strong bg-white/40 backdrop-blur-md p-10 sm:p-14 text-center">
          <p className="font-sans text-body text-text-secondary">{t('player.no_video')}</p>
        </section>
      ) : (
        <section className="dr-card-static overflow-hidden p-2 sm:p-3">
          <div className="rounded-2xl overflow-hidden">
            <EpisodePlayer
              episodeId={episode.id}
              videoDurationSeconds={episode.videoDurationSeconds}
              onCompleted={() => {
                queryClient.invalidateQueries({ queryKey: ['portal', 'course', slug] });
                queryClient.invalidateQueries({ queryKey: ['portal', 'enrollments'] });
              }}
            />
          </div>
        </section>
      )}

      {/* Navigation strip */}
      <section className="dr-card-static p-3 sm:p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          {prevEpisode ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                navigate(`/${locale}/portal/kurzy/${slug}/lekcia/${prevEpisode.displayOrder}`)
              }
            >
              <ChevronLeft size={16} />
              {t('player.previous_lesson')}
            </Button>
          ) : (
            <span className="h-9" />
          )}

          <Button
            variant={isCompleted ? 'secondary' : 'primary'}
            size="sm"
            onClick={() => markCompleteMutation.mutate()}
            disabled={markCompleteMutation.isPending || isCompleted}
          >
            <Check size={16} />
            {isCompleted ? t('player.marked_complete') : t('player.mark_complete')}
          </Button>

          {nextEpisode ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                navigate(`/${locale}/portal/kurzy/${slug}/lekcia/${nextEpisode.displayOrder}`)
              }
            >
              {t('player.next_lesson')}
              <ChevronRight size={16} />
            </Button>
          ) : (
            <span className="h-9" />
          )}
        </div>
      </section>

      {/* Description */}
      {episode.descriptionMd && (
        <section className="dr-card-static p-6 sm:p-8">
          <p className="dr-eyebrow mb-4">
            {t('player.description_eyebrow', { defaultValue: 'O lekcii' })}
          </p>
          <Markdown source={episode.descriptionMd} />
        </section>
      )}

      {/* Resources */}
      {episode.resources.length > 0 && (
        <section className="dr-card-static p-6 sm:p-8">
          <div className="flex items-center justify-between gap-3 mb-5">
            <div>
              <p className="dr-eyebrow">
                {t('courses.portal_resources_eyebrow', { defaultValue: 'Materiály' })}
              </p>
              <h2 className="mt-1 font-display text-h4 text-primary-dark">
                {t('courses.portal_resources_title')}
              </h2>
            </div>
            <span className="font-sans text-caption text-text-muted">
              {episode.resources.length}
            </span>
          </div>
          <ul className="space-y-2.5">
            {episode.resources.map((r) => (
              <li
                key={r.id}
                className="group flex items-center justify-between gap-3 rounded-2xl bg-white/55 backdrop-blur-md border border-glass-border hover:border-primary/30 hover:bg-white/80 px-4 py-3 transition-all"
              >
                <span className="flex items-center gap-3 min-w-0">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 border border-primary/15 text-primary shrink-0">
                    <FileDown size={15} />
                  </span>
                  <span className="font-sans text-body-small text-text-primary truncate">
                    {r.title}
                  </span>
                </span>
                <a
                  href={`/api/v1/episodes/${episode.id}/resources/${r.id}/download`}
                  className="inline-flex items-center gap-1.5 rounded-pill bg-white/80 border border-glass-border-strong text-primary hover:bg-primary hover:text-white hover:border-primary px-3 h-8 font-sans text-caption font-medium transition-all"
                >
                  {t('courses.portal_resources_download')}
                  <ArrowLeft size={12} className="rotate-[225deg]" />
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function formatUnlockDate(unixSeconds: number, locale: Locale): string {
  const date = new Date(unixSeconds * 1000);
  return date.toLocaleDateString(locale === 'cs' ? 'cs-CZ' : 'sk-SK', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}
